import { Hole, HoleLoad } from 'src/app/services/hole.service';
import { measuredDepth } from './hole-calculations';
import { HoleToleranceThreshold, SiteHoleTolerance, getThreshold } from 'src/app/bo/site-hole-tolerance';

/**
 * Quality metrics of the Holes › Quality view. Ids are the `metric` keys of the
 * site hole tolerance (`Sites/{siteId}/hole-tolerance`), so a threshold is
 * looked up with `getThreshold(tolerance, metric.id)`.
 *
 * Figma legend → metric: Collar position → HEAD, Toe position → TOE_POSITION,
 * Hole's toe → LENGTH (drilled vs designed length), Bulk → BULK, Stemming final → STEMMING.
 * POWDER_FACTOR (bulk explosive / rock volume of the hole's Voronoi cell) was added afterwards; unlike
 * the others it is an absolute value (kg/m³) without site tolerance: holes are coloured on a scale
 * computed from the blast's own values (blue = under loaded … green … red = over loaded).
 */
export type QualityMetricId = 'HEAD' | 'TOE_POSITION' | 'LENGTH' | 'BULK' | 'POWDER_FACTOR' | 'STEMMING';

export interface QualityMetric {
  id:          QualityMetricId;
  labelKey:    string;
  /** One-word name, for the ring chips above the table where the full label does not fit. */
  shortKey:    string;
  /** Unit shown when the site tolerance carries none. */
  defaultUnit: string;
  /** Design metrics come from the blast design, loading metrics need the blast loading query. */
  source:      'design' | 'loading';
}

export const QUALITY_METRICS: readonly QualityMetric[] = [
  { id: 'HEAD',          labelKey: 'HOLES_TAB.QUALITY_METRIC_HEAD',          shortKey: 'HOLES_TAB.QUALITY_METRIC_SHORT_HEAD',          defaultUnit: 'm',      source: 'design' },
  { id: 'TOE_POSITION',  labelKey: 'HOLES_TAB.QUALITY_METRIC_TOE_POSITION',  shortKey: 'HOLES_TAB.QUALITY_METRIC_SHORT_TOE_POSITION',  defaultUnit: 'm',      source: 'design' },
  { id: 'LENGTH',        labelKey: 'HOLES_TAB.QUALITY_METRIC_LENGTH',        shortKey: 'HOLES_TAB.QUALITY_METRIC_SHORT_LENGTH',        defaultUnit: 'm',      source: 'design' },
  { id: 'BULK',          labelKey: 'HOLES_TAB.QUALITY_METRIC_BULK',          shortKey: 'HOLES_TAB.QUALITY_METRIC_SHORT_BULK',          defaultUnit: 'kg',     source: 'loading' },
  { id: 'POWDER_FACTOR', labelKey: 'HOLES_TAB.QUALITY_METRIC_POWDER_FACTOR', shortKey: 'HOLES_TAB.QUALITY_METRIC_SHORT_POWDER_FACTOR', defaultUnit: 'kg/m³', source: 'loading' },
  { id: 'STEMMING',      labelKey: 'HOLES_TAB.QUALITY_METRIC_STEMMING',      shortKey: 'HOLES_TAB.QUALITY_METRIC_SHORT_STEMMING',      defaultUnit: 'm',      source: 'loading' },
];

export const DEFAULT_QUALITY_METRIC: QualityMetricId = 'HEAD';

export function isQualityMetricId(value: unknown): value is QualityMetricId {
  return QUALITY_METRICS.some(metric => metric.id === value);
}

export type QualityBucket = 'asDesigned' | 'inTolerance' | 'outOfSpec';

export const QUALITY_BUCKETS: readonly QualityBucket[] = ['asDesigned', 'inTolerance', 'outOfSpec'];

export const QUALITY_BUCKET_COLORS: Readonly<Record<QualityBucket, string>> = {
  asDesigned:  '#1CC254',
  inTolerance: '#FAC70F',
  outOfSpec:   '#DF2A2A',
};

export const QUALITY_BUCKET_LABEL_KEYS: Readonly<Record<QualityBucket, string>> = {
  asDesigned:  'HOLES_TAB.QUALITY_AS_DESIGNED',
  inTolerance: 'HOLES_TAB.QUALITY_IN_TOLERANCE',
  outOfSpec:   'HOLES_TAB.QUALITY_OUT_OF_SPEC',
};

/** Colour of a hole without a value for the selected metric. */
export const QUALITY_NO_DATA_COLOR = '#ACADAF';

/**
 * Value of a hole for a metric, or null when it cannot be computed: the signed deviation
 * measured − planned for most metrics (positional ones are distances, hence always ≥ 0), the
 * absolute powder factor for POWDER_FACTOR.
 * @param volume rock volume of the hole (m³, see `holeVolume`), only used by POWDER_FACTOR
 */
export function qualityValue(metric: QualityMetricId, hole: Hole, load: HoleLoad | undefined, volume: number | null = null): number | null {
  switch (metric) {
    // The calculated magnitudes are in cm (legacy State columns); tolerances and the Quality columns are in metres
    case 'HEAD':         return centimetresToMetres(hole.calculatedValues?.headPositionMagnitude);
    case 'TOE_POSITION': return centimetresToMetres(hole.calculatedValues?.toePositionMagnitude);
    case 'LENGTH': {
      const designed = finite(hole.Geometry?.Design?.length);
      const actual   = measuredDepth(hole);
      return designed == null || actual == null ? null : actual - designed;
    }
    case 'BULK':
      return positiveDelta(load?.plannedTotalBulk, load?.measuredTotalBulk);
    case 'POWDER_FACTOR':
      return powderFactor(bestBulk(load), volume);
    case 'STEMMING':
      return positiveDelta(load?.plannedSteaming, load?.measuredSteaming);
  }
}

/**
 * Bucket of a deviation against a threshold. Negative deviations use the threshold's
 * negative side when it is configured, otherwise the magnitude is compared to the positive side
 * (the legacy behaviour). Null when there is no value or no threshold.
 */
export function qualityBucket(value: number | null, threshold: HoleToleranceThreshold | undefined): QualityBucket | null {
  if (value == null || !threshold) return null;

  if (value < 0 && threshold.asDesignedMin != null && threshold.inToleranceMin != null) {
    if (value > threshold.asDesignedMin)   return 'asDesigned';
    if (value >= threshold.inToleranceMin) return 'inTolerance';
    return 'outOfSpec';
  }

  const magnitude = Math.abs(value);
  if (magnitude < threshold.asDesignedMax)   return 'asDesigned';
  if (magnitude <= threshold.inToleranceMax) return 'inTolerance';
  return 'outOfSpec';
}

/** True when the threshold defines a negative side (length-like metrics). */
export function hasNegativeSide(threshold: HoleToleranceThreshold | undefined): boolean {
  return threshold?.asDesignedMin != null && threshold?.inToleranceMin != null;
}

export function thresholdFor(tolerance: SiteHoleTolerance | null | undefined, metric: QualityMetricId): HoleToleranceThreshold | undefined {
  const threshold = getThreshold(tolerance, metric);
  return threshold && threshold.asDesignedMax > 0 ? threshold : undefined;
}

export function countByBucket(buckets: Iterable<QualityBucket | null>): Record<QualityBucket | 'noData', number> {
  const counts = { asDesigned: 0, inTolerance: 0, outOfSpec: 0, noData: 0 };
  for (const bucket of buckets) counts[bucket ?? 'noData']++;
  return counts;
}

// ── Powder factor ─────────────────────────────────────────────────────────────

/** Best known length of a hole: mined (measured), else drilled, else planned. Null when none is > 0. */
export function bestHoleLength(hole: Hole): number | null {
  const geometry = hole.Geometry;
  for (const candidate of [geometry?.Measure?.length, geometry?.drilllength, geometry?.Design?.length]) {
    const length = finite(candidate);
    if (length != null && length > 0) return length;
  }
  return null;
}

/**
 * Rock volume affected by a hole (m³): the area of its Voronoi cell (m², cut to the blast
 * contour) times its best known length. Null without a cell or a length.
 */
export function holeVolume(hole: Hole, cellArea: number | null | undefined): number | null {
  const area   = finite(cellArea);
  const length = bestHoleLength(hole);
  return area != null && area > 0 && length != null ? area * length : null;
}

/** Bulk explosive actually in the hole: the measured mass when known (> 0), else the planned one. */
export function bestBulk(load: HoleLoad | undefined): number | null {
  const measured = finite(load?.measuredTotalBulk);
  if (measured != null && measured > 0) return measured;
  const planned = finite(load?.plannedTotalBulk);
  return planned != null && planned > 0 ? planned : null;
}

/** Powder factor (kg/m³): bulk explosive mass over the rock volume. Null unless both are > 0. */
export function powderFactor(bulkKg: number | null | undefined, volume: number | null | undefined): number | null {
  const mass = finite(bulkKg);
  const cubic = finite(volume);
  return mass != null && cubic != null && mass > 0 && cubic > 0 ? mass / cubic : null;
}

/** Colour stops of the powder factor scale: under loaded (blue) → balanced (green) → over loaded (red). */
export const POWDER_FACTOR_SCALE_COLORS = {
  low:  '#2F80ED',
  mid:  '#1CC254',
  high: '#DF2A2A',
} as const;

export interface PowderFactorScale {
  min: number;
  mid: number;
  max: number;
}

/** Scale of a blast: min / max of its powder factors, green at the midpoint. Null without values. */
export function powderFactorScale(values: Iterable<number | null>): PowderFactorScale | null {
  let min = Infinity;
  let max = -Infinity;
  for (const value of values) {
    if (value == null || !Number.isFinite(value)) continue;
    if (value < min) min = value;
    if (value > max) max = value;
  }
  if (min === Infinity) return null;
  return { min, mid: (min + max) / 2, max };
}

/** Colour of a powder factor on the blast scale (linear blue → green → red); the mid colour when the scale is flat. */
export function powderFactorColor(value: number | null, scale: PowderFactorScale | null): string | null {
  if (value == null || !scale) return null;
  if (scale.max <= scale.min) return POWDER_FACTOR_SCALE_COLORS.mid;
  if (value <= scale.mid) {
    return mixHex(POWDER_FACTOR_SCALE_COLORS.low, POWDER_FACTOR_SCALE_COLORS.mid, (value - scale.min) / (scale.mid - scale.min));
  }
  return mixHex(POWDER_FACTOR_SCALE_COLORS.mid, POWDER_FACTOR_SCALE_COLORS.high, (value - scale.mid) / (scale.max - scale.mid));
}

/** Linear RGB mix of two `#rrggbb` colours, `t` clamped to [0, 1]. */
function mixHex(from: string, to: string, t: number): string {
  const ratio = Math.min(1, Math.max(0, t));
  const a = parseInt(from.slice(1), 16);
  const b = parseInt(to.slice(1), 16);
  const channel = (shift: number) => Math.round(((a >> shift) & 0xff) * (1 - ratio) + ((b >> shift) & 0xff) * ratio);
  return `#${[16, 8, 0].map(shift => channel(shift).toString(16).padStart(2, '0')).join('')}`;
}

// ── internals ─────────────────────────────────────────────────────────────────

function finite(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function centimetresToMetres(value: number | null | undefined): number | null {
  const cm = finite(value);
  return cm == null ? null : cm / 100;
}

/** Loading quantities are only meaningful when both planned and measured are known (> 0). */
function positiveDelta(planned: number | null | undefined, measured: number | null | undefined): number | null {
  const p = finite(planned);
  const m = finite(measured);
  return p != null && m != null && p > 0 && m > 0 ? m - p : null;
}
